import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, ExternalLink } from 'lucide-react';
import '../styles/TrainingView.css';
import '../styles/SavedQuestions.css';
import '../styles/contests/ListeningMaster.css';
import StreakUpgradeButton from './StreakUpgradeButton';
import { API_URL } from '../app/constants'

const Saved = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealedAnswers, setRevealedAnswers] = useState({});
  const [showUnsaveModal, setShowUnsaveModal] = useState(false);
  const [questionToUnsave, setQuestionToUnsave] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  
  const categories = [
    { value: 'all', label: 'All' },
    { value: 'vocabulary', label: 'Vocabulary' },
    { value: 'grammar', label: 'Grammar' },
    { value: 'reading', label: 'Reading' }
  ];


  // Fetch saved questions from API
  const fetchSavedQuestions = useCallback(async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const categoryParam = selectedCategory === 'all' ? '' : `?category=${selectedCategory}`;
      const response = await fetch(`${API_URL}/api/v1/panel/saved-questions${categoryParam}`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
        method: "GET",
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        setError("Error loading saved questions");
        return [];
      }
    } catch (error) {
      console.error("Error fetching saved questions:", error);
      setError("Error loading saved questions");
      return [];
    }
  }, [selectedCategory]);

  useEffect(() => {
    const loadSavedQuestions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await fetchSavedQuestions();
        console.log("Final data being set to state:", data);
        setSavedQuestions(data);
      } catch (error) {
        console.error("Error loading saved questions:", error);
        setError("Error loading saved questions");
      } finally {
        setLoading(false);
      }
    };

    loadSavedQuestions();
  }, [selectedCategory, fetchSavedQuestions]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);
  };

  const toggleReveal = (questionId) => {
    setRevealedAnswers((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const handleUnsave = (questionId) => {
    setQuestionToUnsave(questionId);
    setShowUnsaveModal(true);
  };

  const confirmUnsave = async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/saved-questions/${questionToUnsave}`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
        method: "DELETE",
      });
      
      if (response.ok) {
        // Remove from local state
        setSavedQuestions(prev => prev.filter(q => q.id !== questionToUnsave));
        setShowUnsaveModal(false);
      } else {
        console.error("Error unsaving question");
      }
    } catch (error) {
      console.error("Error unsaving question:", error);
    }
  };

  // Empty state
  if (!loading && savedQuestions.length === 0) {
    return (
      <div className="training-view">
        <StreakUpgradeButton source="Saved" />
        <div className="dashboard-header">
          <h1>Saved</h1>
        </div>
        
        <div className="session-selector">
          <button 
            className="session-button"
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
          >
            {categories.find(cat => cat.value === selectedCategory)?.label || "Select Category"}
            <svg className={`arrow-icon ${showCategoryDropdown ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div className={`session-dropdown ${showCategoryDropdown ? 'show' : ''}`}>
            {categories.map((category) => (
              <button
                key={category.value}
                className={`session-option ${selectedCategory === category.value ? 'active' : ''}`}
                onClick={() => handleCategorySelect(category.value)}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        <div className="empty-state" style={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: '16px',
          color: '#6B7280',
          textAlign: 'center',
          padding: '80px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}>
          You don't have any saved questions yet!
        </div>
      </div>
    );
  }

  return (
    <div className="training-view">
      <StreakUpgradeButton source="Saved" />
      <div className="dashboard-header">
        <h1>Saved</h1>
      </div>
      
      <div className="session-selector">
        <button 
          className="session-button"
          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
        >
          {categories.find(cat => cat.value === selectedCategory)?.label || "Select Category"}
          <svg className={`arrow-icon ${showCategoryDropdown ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className={`session-dropdown ${showCategoryDropdown ? 'show' : ''}`}>
          {categories.map((category) => (
            <button
              key={category.value}
              className={`session-option ${selectedCategory === category.value ? 'active' : ''}`}
              onClick={() => handleCategorySelect(category.value)}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner-ripple">
            <div></div>
            <div></div>
          </div>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : (
        <div className="saved-questions-list">
          {console.log("Rendering saved questions:", savedQuestions)}
          {savedQuestions.map((question, index) => {
            console.log("Individual question:", question);
            return (
            <div 
              key={question.id}
              className="saved-question-card"
            >
              {/* Question Display using exact QuestionDisplay styling */}
              <div className="sqd-question-display">
                <div className="sqd-question-header">
                  <div className="sqd-question-title">{question.title}</div>
                  <div className="sqd-header-buttons">
                    <button 
                      className="sqd-show-answer-btn"
                      onClick={() => toggleReveal(question.id)}
                    >
                      {revealedAnswers[question.id] ? 'Hide Answer' : 'Show Answer'}
                    </button>
                    <button 
                      className="sqd-bookmark-btn saved"
                      onClick={() => handleUnsave(question.id)}
                      aria-label="Remove from saved"
                    >
                      <Bookmark size={18} fill="currentColor" />
                    </button>
                  </div>
                </div>
                
                {question.instruction && (
                  <div className="sqd-general-instruction">
                    {question.instruction}
                  </div>
                )}

                {question.script && question.script !== "0" && (
                  <div className="sqd-script-text">
                    {question.script}
                  </div>
                )}

                {question.question_urls && question.question_urls.urls && question.question_urls.urls.length > 0 && (
                  <div className="sqd-image-container">
                    {question.question_urls.urls.map((url, index) => (
                      <div key={index} className="sqd-image-wrapper">
                        <img 
                          src={url} 
                          alt={`Question ${index + 1}`} 
                          className="sqd-question-image"
                        />
                        <button 
                          className="sqd-zoom-button"
                          onClick={() => setZoomedImage(url)}
                          title="Open image in full size"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {question.script_image && question.script_image.urls && question.script_image.urls.length > 0 && (
                  <div className="sqd-image-container">
                    {question.script_image.urls.map((url, index) => (
                      <div key={index} className="sqd-image-wrapper">
                        <img 
                          src={url} 
                          alt={`Script ${index + 1}`} 
                          className="sqd-question-image"
                        />
                        <button 
                          className="sqd-zoom-button"
                          onClick={() => setZoomedImage(url)}
                          title="Open image in full size"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="sqd-question-text">
                  {question.question}
                </div>

                <div className="sqd-answer-options">
                  {question.options.map((option, optionIndex) => {
                    const optionText = option;
                    const isCorrect = revealedAnswers[question.id] && (optionIndex + 1 === parseInt(question.correct_answer));
                    
                    return (
                      <div
                        key={optionIndex}
                        className={`sqd-answer-option ${
                          isCorrect ? 'correct' : ''
                        }`}
                      >
                        <span className="sqd-answer-number">{optionIndex + 1}</span>
                        <span className="sqd-answer-text">{optionText}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )})}
        </div>
      )}

      {/* Unsave Modal - Same design as PersonalizedExam exit modal */}
      {showUnsaveModal && (
        <div className="listening-master-modal-overlay">
          <div className="listening-master-modal-content">
            <h3>Remove Saved Question</h3>
            <p>Are you sure you want to remove this question from your saved items?</p>
            <div className="listening-master-modal-buttons">
              <button 
                className="listening-master-modal-cancel"
                onClick={() => setShowUnsaveModal(false)}
              >
                Cancel
              </button>
              <button 
                className="listening-master-modal-confirm"
                onClick={confirmUnsave}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="sqd-image-modal" onClick={() => setZoomedImage(null)}>
          <button 
            className="sqd-image-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              setZoomedImage(null);
            }}
          >
            ×
          </button>
          <img src={zoomedImage} alt="Zoomed" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Saved;